import type { FeedArticleProjectionData } from "@/data-access/shared/fragments/feed-article-projection-fragments";

export {
  formatNumber,
  formattedDateToUTC,
} from "@/lib/utils/feedArticleFormat";

export const hasSecondSection = (article: FeedArticleProjectionData) => {
  return !!(article.summary || article.firstMention || article.searchMatch);
};

export const transformSummary = (summary: string): string[] => {
  return summary.split("\n").filter((line) => line.trim() !== "");
};

/**
 * Escapes HTML special characters to prevent XSS attacks
 */
export const escapeHtml = (unsafe: string | null | undefined): string => {
  if (unsafe == null) return "";
  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

export interface ParseSearchMatchResult {
  mention: string;
  /** First term (for backward compatibility, e.g. trim logic). */
  term: string;
  /** All highlighted terms in document order. */
  terms: string[];
}

/**
 * Parses searchMatch string to extract search hit information.
 * Extracts every <em>...</em> (or <em><b>...</b></em>) segment as a term so multiple
 * highlights are supported (e.g. "<em>Astera</em> <em>Labs</em> Appoints...").
 * Applies HTML escaping for defense in depth against XSS.
 */
export const parseSearchMatch = (
  searchMatch: string | null | undefined,
): ParseSearchMatchResult | undefined => {
  if (!searchMatch) return undefined;

  const termRaws: string[] = [];
  const re1 = /<em><b>(.*?)<\/b><\/em>/g;
  for (
    let match = re1.exec(searchMatch);
    match !== null;
    match = re1.exec(searchMatch)
  ) {
    const captured = match[1];
    if (captured !== undefined) termRaws.push(captured);
  }

  if (termRaws.length === 0) {
    const re2 = /<em>(.*?)<\/em>/g;
    for (
      let match = re2.exec(searchMatch);
      match !== null;
      match = re2.exec(searchMatch)
    ) {
      const captured = match[1];
      if (captured !== undefined) termRaws.push(captured);
    }
  }

  if (termRaws.length === 0) return undefined;

  const mentionRaw = searchMatch.replace(/<.*?>/g, "");
  const mention = escapeHtml(mentionRaw);
  const terms = termRaws.map((raw) => escapeHtml(raw));

  return {
    mention,
    term: terms[0] ?? "",
    terms,
  };
};

/**
 * Determines if the sentiment is AI-generated (not overridden)
 * Returns true if sentimentPolarity matches sentimentPolarityOriginal
 * Returns false if they differ or if either value is null
 */
export const isAISentiment = (
  sentimentPolarity: string | null | undefined,
  sentimentPolarityOriginal: string | null | undefined,
): boolean => {
  if (sentimentPolarityOriginal == null || sentimentPolarity == null) {
    return false;
  }
  return (
    sentimentPolarity.toLowerCase() === sentimentPolarityOriginal.toLowerCase()
  );
};
